import { useEffect, useMemo, useState } from "react";
import { useRootStore } from "../hooks/useRootStore";
import { Avatar, Box, Card, CardHeader, CircularProgress, Link, Table, TableBody, TableCell, TableRow, Tooltip } from "@mui/material";
import classes from './Dashbord.module.scss';
import { EmployeeProfile } from "../data/UserProfile";
import { IssueListFilterModel, IssueListItem } from "./IssueList";
import { getIssueStateNameRu, IssueState } from "../data/IssueState";
import moment from "moment";
import { getIssuePriorityNameRu } from "../data/IssuePriority";
import FlagIcon from '@mui/icons-material/Flag';
import { UserType } from "../data/UserType";
import { getPersonInitials, Person } from "../data/Person";
import { BalanceTransaction } from "../data/BalanceTransaction";
import AgentDebtsDialog from "./AgentDebtsDialog";
import useDeviceMedia from '../hooks/useDeviceMedia';
import { formatter } from "../utils.ts";

export default function Dashboard() {
    const { api, userProfile } = useRootStore();
    const { isMobilePortrait } = useDeviceMedia();

    const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
    const [issuesLoaded, setIssuesLoaded] = useState(false);
    const [transactionsLoaded, setTransactionsLoaded] = useState(false);
    const [balanceLoaded, setBalanceLoaded] = useState(false);
    const [vacationBalanceLoaded, setVacationBalanceLoaded] = useState(false);
    const [myMonthHoursLoaded, setMyMonthHoursLoaded] = useState(false);
    const [myTodayHoursLoaded, setMyTodayHoursLoaded] = useState(false);
    const [myYesterdayHoursLoaded, setMyYesterdayHoursLoaded] = useState(false);
    const [myThirtyDaysHoursLoaded, setMyThirtyDaysHoursLoaded] = useState(false);
    const [authorMonthHoursLoaded, setAuthorMonthHoursLoaded] = useState(false);
    const [authorTodayHoursLoaded, setAuthorTodayHoursLoaded] = useState(false);
    const [errorsLoaded, setErrorsLoaded] = useState(false);
    const [queueLengthLoaded, setQueueLengthLoaded] = useState(false);

    const [myMonthHours, setMyMonthHours] = useState(0);
    const [queueLength, setQueueLength] = useState(0);
    const [myTodayHours, setMyTodayHours] = useState(0);
    const [myYesterdayHours, setMyYesterdayHours] = useState(0);
    const [balance, setBalance] = useState(0);
    const [vacationBalance, setVacationBalance] = useState(0);
    const [transactions, setTransactions] = useState([] as BalanceTransaction[]);
    const [myThirtyDaysHours, setMyThirtyDaysHours] = useState(0);
    
    const [authorMonthHours, setAuthorMonthHours] = useState({} as { ErrorsHours: number, CriticalHours: number, PriorityHours: number, RegularHours: number, TotalHours: number, });
    const [authorTodayHours, setAuthorTodayHours] = useState({} as { ErrorsHours: number, CriticalHours: number, PriorityHours: number, RegularHours: number, TotalHours: number, });
    const [issues, setIssues] = useState([] as IssueListItem[]);
    const [errors, setErrors] = useState([] as IssueListItem[]);

    const now = new Date();

    const transactionsPeriod = {
        from: moment(now).add(-1, 'month').startOf('month').toDate(),
        to: moment(now).add(1, 'day').toDate(),
    }

    useEffect(() => {
        api.getIssues({
            states: [
                IssueState.accepted,
                IssueState.assigned,
                IssueState.backlog,
                IssueState.commentsWaiting,
                IssueState.criticalWaiting,
                IssueState.queued,
                IssueState.testing,
                IssueState.available,
                //IssueState.completed,
            ],
            
            meMode: true,
            offset: 0,
        } as IssueListFilterModel).then(i => {
            setIssues(i.Records);
            setIssuesLoaded(true);
        });

        api.getMyBalance().then(b => {
            setBalance(b);
            setBalanceLoaded(true);
        });

        api.getQueueLength().then(l => {
            setQueueLength(l);
            setQueueLengthLoaded(true);
        });

        api.getMyBalanceTransactions(transactionsPeriod.from, transactionsPeriod.to)
            .then(ts => {
                setTransactions(ts);
                setTransactionsLoaded(true);
            });

        api.getMyVacationBalance().then(vb => {
                setVacationBalance(vb);
                setVacationBalanceLoaded(true);
            });

        api.getIssues({
            states: [
                IssueState.accepted,
                IssueState.assigned,
                IssueState.backlog,
                IssueState.commentsWaiting,
                IssueState.criticalWaiting,
                IssueState.queued,
                IssueState.testing,
                IssueState.available,
                //IssueState.completed,
            ],

            myErrors: true,
            datesCreate: { from: moment(now).startOf('month').toDate(), to: moment(now).add(1, 'month').startOf('month').add(-1, 'day').toDate() },
            meMode: false,
            offset: 0,
        } as IssueListFilterModel).then(i => {
            setErrors(i.Records);
            setErrorsLoaded(true);
        });

        api.getEmployeeHours(
            moment(now).startOf('month').toDate(),
            moment(now).add(1, 'month').startOf('month').toDate()).then(i =>
            {
                setMyMonthHours(i);
                setMyMonthHoursLoaded(true);
            });

        api.getEmployeeHours(
            moment(now).startOf('day').toDate(),
            moment(now).add(1, 'day').toDate()).then(i => {
                setMyTodayHours(i);
                setMyTodayHoursLoaded(true);
            });

        api.getEmployeeHours(
            moment(now).startOf('day').add(-1, 'day').toDate(),
            moment(now).startOf('day').toDate()).then(i => {
                setMyYesterdayHours(i);
                setMyYesterdayHoursLoaded(true);
            });

        api.getEmployeeHours(
            moment(now).startOf('day').add(-30, 'day').toDate(),
            moment(now).add(1, 'day').toDate()).then(i => {
                setMyThirtyDaysHours(i);
                setMyThirtyDaysHoursLoaded(true);
            });

        api.getAuthorHours(
            moment(now).startOf('month').toDate(),
            moment(now).add(1, 'month').startOf('month').add(-1, 'day').toDate()).then(i => {
                setAuthorMonthHours(i);
                setAuthorMonthHoursLoaded(true);
            });

        api.getAuthorHours(
            moment(now).startOf('day').toDate(),
            moment(now).add(1, 'day').toDate()).then(i => {
                setAuthorTodayHours(i);
                setAuthorTodayHoursLoaded(true);
            });

    }, [userProfile?.ID, api]);

    const profile = (userProfile as EmployeeProfile);
    const activeIssue = profile.ActiveIssueId ? issues.filter(x => x.ID == profile.ActiveIssueId)[0] : null;

    const assignedIssues = useMemo(() => issues
        .filter(x =>
            ![IssueState.queued, IssueState.backlog, IssueState.available ].includes(x.StateID)
            && x.AssignedEmployee?.ID == userProfile!.ID)
        .sort((a, b) =>
            a.StateID == b.StateID ? 0
                : a.StateID == IssueState.commentsWaiting || a.StateID == IssueState.criticalWaiting ? 1
                    : b.StateID == IssueState.commentsWaiting || b.StateID == IssueState.criticalWaiting ? -1
                        : Math.sign(a.StateID - b.StateID))        ,
        [issues, userProfile]
    )

    const myIssues = useMemo(() => issues
        .filter(x => x.AuthorID == userProfile!.ID)
        .sort((a, b) =>
            a.StateID == b.StateID ? 0
                : a.StateID == IssueState.backlog ? 1
                    : b.StateID == IssueState.backlog ? -1
                        : Math.sign(a.StateID - b.StateID)),
        [issues, userProfile]
    );

    const myAvailableIssues = useMemo(() => myIssues.filter(x =>
        x.StateID == IssueState.available),
        [myIssues]
    );

    const myPausedForCommentsIssues = useMemo(() => myIssues.filter(x =>
        x.StateID == IssueState.commentsWaiting),
        [myIssues]
    );

    const moneyIncomes = useMemo(() => transactions.filter(t => t.Amount > 0),
        [transactions]
    );

    const moneyOutcomes = useMemo(() => transactions.filter(t => t.Amount < 0).map(x => { return { ...x, Amount: -x.Amount } }),
        [transactions]
    );

    const moneyIncomesTotal = useMemo(() => moneyIncomes.length == 0 ? 0 : moneyIncomes.reduce((total, a) => total + a.Amount, 0), [moneyIncomes]);

    const moneyOutcomesTotal = useMemo(() => moneyOutcomes.length == 0 ? 0 : moneyOutcomes.reduce((total, a) => total + a.Amount, 0), [moneyOutcomes]);

    return (
        <Box className={classes.dashboard}>
            {transactionsDialogOpen && <AgentDebtsDialog period={transactionsPeriod} transactions={transactions} onClose={() => setTransactionsDialogOpen(false)} />}
            <Box className={classes.dashboardAside}>
                {userProfile!.UserType == UserType.CustomerEmployee ?
                    <>
                        <Box className={classes.crd} sx={{ flex: 'none', whiteSpace: 'nowrap', }}>
                            Общая очередь: {queueLengthLoaded ? queueLength + ' задач' : 'подсчет...'}
                        </Box>
                        <Box className={classes.crd} sx={{ flex: 'none', whiteSpace: 'nowrap', }}>
                            <CardHeader sx={{ display: "inline-block", }} title="Записанные часы:" className={classes.cardHeader} />
                            <Table sx={{ mt: 0.5, flex: '0', width: 'auto' }}>
                                <TableBody>
                                    <TableRow><TableCell>За сегодня:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!authorTodayHoursLoaded ? 'подсчет...' : moment.duration(authorTodayHours.TotalHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{pl:'15px !important'}}>Ошибок:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!authorTodayHoursLoaded ? 'подсчет...' : moment.duration(authorTodayHours.ErrorsHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ pl: '15px !important' }}>Критических:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!authorTodayHoursLoaded ? 'подсчет...' : moment.duration(authorTodayHours.CriticalHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ pl: '15px !important' }}>Приоритетных:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!authorTodayHoursLoaded ? 'подсчет...' : moment.duration(authorTodayHours.PriorityHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ pl: '15px !important' }}>Обычных:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!authorTodayHoursLoaded ? 'подсчет...' : moment.duration(authorTodayHours.RegularHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell>С начала месяца:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!authorMonthHoursLoaded ? 'подсчет...' : moment.duration(authorMonthHours.TotalHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ pl: '15px !important' }}>Ошибок:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!authorTodayHoursLoaded ? 'подсчет...' : moment.duration(authorMonthHours.ErrorsHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ pl: '15px !important' }}>Критических:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!authorTodayHoursLoaded ? 'подсчет...' : moment.duration(authorMonthHours.CriticalHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ pl: '15px !important' }}>Приоритетных:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!authorTodayHoursLoaded ? 'подсчет...' : moment.duration(authorMonthHours.PriorityHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ pl: '15px !important' }}>Обычных:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!authorTodayHoursLoaded ? 'подсчет...' : moment.duration(authorMonthHours.RegularHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                        <Box className={classes.crd} sx={{ flex: 'none', whiteSpace: 'nowrap', }}>
                            <CardHeader sx={{ display: "inline-block", }} title="Задачи по статусам:" className={classes.cardHeader} />
                            <Table sx={{ mt: 0.5, flex: '0', width: 'auto' }}>
                                <TableBody>
                                    <TableRow><TableCell>Беклог:</TableCell><TableCell sx={{ textAlign: 'right' }}>{myIssues.filter(x => x.StateID == IssueState.backlog).length}</TableCell></TableRow>
                                    <TableRow><TableCell>Очередь:</TableCell><TableCell sx={{ textAlign: 'right' }}>{myIssues.filter(x => x.StateID == IssueState.queued).length}</TableCell></TableRow>
                                    <TableRow><TableCell>Готовых:</TableCell><TableCell sx={{ textAlign: 'right' }}>{myIssues.filter(x => x.StateID == IssueState.available).length}</TableCell></TableRow>
                                    <TableRow><TableCell>Назначенных:</TableCell><TableCell sx={{ textAlign: 'right' }}>{myIssues.filter(x => x.StateID == IssueState.assigned).length}</TableCell></TableRow>
                                    <TableRow><TableCell>В работе:</TableCell><TableCell sx={{ textAlign: 'right' }}>{myIssues.filter(x => x.StateID == IssueState.accepted).length}</TableCell></TableRow>
                                    <TableRow><TableCell>В тестировании:</TableCell><TableCell sx={{ textAlign: 'right' }}>{myIssues.filter(x => x.StateID == IssueState.testing).length}</TableCell></TableRow>
                                    <TableRow><TableCell>Ждут комментариев:</TableCell><TableCell sx={{ textAlign: 'right' }}>{myIssues.filter(x => x.StateID == IssueState.commentsWaiting).length}</TableCell></TableRow>
                                    <TableRow><TableCell>Отложены:</TableCell><TableCell sx={{ textAlign: 'right' }}>{myIssues.filter(x => x.StateID == IssueState.criticalWaiting).length}</TableCell></TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                    :
                    <>
                        <Box className={[classes.crd, classes.dashboardActiveIssue].join(' ')} sx={{ position: 'relative' }}>
                            {!issuesLoaded ? <CircularProgress />
                                : !activeIssue ? 'Нет активной задачи' :
                                    <Box sx={isMobilePortrait ? {} : { maxWidth: "280px", minWidth: "280px" }}>
                                        <Link sx={{ pr: '20px', display: "inline-block", m: 0, p: 0 }} href={`issues/${activeIssue.ID}`}>
                                            <CardHeader style={{ fontSize: 'x-small', marginBottom:0 }} className={classes.cardHeader} title={activeIssue.Subject.length > 25 ? activeIssue.Subject.substring(0, 25) + '...' : activeIssue.Subject} />
                                        </Link>
                                        <Box className={[classes.issuePriority, 'issue-priority', `priority-${activeIssue.PriorityID}`].join(' ')} sx={{ position: 'absolute', right: '6px', top: '6px' }}>
                                            <Tooltip
                                                arrow
                                                disableInteractive
                                                title={activeIssue.IsError ? 'ошибка' : getIssuePriorityNameRu(activeIssue.PriorityID) ?? activeIssue.PriorityID}
                                            >
                                                <FlagIcon sx={{ width: '20px', height: '20px' }} />
                                            </Tooltip>
                                        </Box>
                                        <Table sx={{ mt: 0.5, flex: '0' }}>
                                            <TableRow><TableCell>Назначена:</TableCell><TableCell sx={{ textAlign: 'right' }}>{moment(activeIssue.CreationDate).format('DD.MM.YYYY')}</TableCell></TableRow>
                                            <TableRow><TableCell>Дедлайн:</TableCell><TableCell sx={{ textAlign: 'right' }}>{activeIssue.EstimatedDate ? moment(activeIssue.EstimatedDate).format('DD.MM.YYYY') : 'не определено'}</TableCell></TableRow>
                                            <TableRow><TableCell sx={{ pr: '30px !important' }}>План/Факт, ч:</TableCell><TableCell sx={{ textAlign: 'right' }}>{activeIssue.EstimationHours}:00 / {moment.duration(activeIssue.TrackedHours * 60 * 60 * 1000).format("h:mm", { trim: false })}</TableCell></TableRow>
                                        </Table>
                                    </Box>
                            }
                        </Box>
                        <Box className={classes.crd} sx={{ flex: 'none', whiteSpace: 'nowrap', }}>
                            <CardHeader sx={{ display: "inline-block", }} title="Записанные часы:" className={classes.cardHeader} />
                            <Table sx={{ mt: 0.5, flex: '0' }}>
                                <TableBody>
                                    <TableRow><TableCell>За сегодня:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!myTodayHoursLoaded ? 'подсчет...' : moment.duration(myTodayHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell>За вчера:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!myYesterdayHoursLoaded ? 'подсчет...' : moment.duration(myYesterdayHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ pr: '30px !important' }}>С начала месяца:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!myMonthHoursLoaded ? 'подсчет...' : moment.duration(myMonthHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                    <TableRow><TableCell>За 30 дней:</TableCell><TableCell sx={{ textAlign: 'right' }}>{!myThirtyDaysHoursLoaded ? 'подсчет...' : moment.duration(myThirtyDaysHours * 60 * 60 * 1000).format("hh:mm", { trim: false })}</TableCell></TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                        <Box className={classes.crd} sx={{ flex: 'none', whiteSpace: 'nowrap', cursor: 'pointer', }} onClick={() => setTransactionsDialogOpen(true)}>
                            <CardHeader sx={{ display: "inline-block", }} title="Деньги" className={classes.cardHeader} />
                            <Table sx={{ mt: 0.5, flex: '0' }}>
                                <TableBody>
                                    <TableRow><TableCell sx={{ pr: '30px !important' }}>Баланс:</TableCell><TableCell sx={{ textAlign: 'right' }}>{balanceLoaded ? formatter.format(balance) : 'подсчет...'}</TableCell></TableRow>
                                    <TableRow><TableCell colSpan={2} style={{ paddingTop: 20 }}>Движения с {moment(transactionsPeriod.from).format('DD.MM.yyyy')}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ pr: 2 }} style={{ paddingLeft: 15 }}>Приход:</TableCell><TableCell sx={{ textAlign: 'right' }}>{transactionsLoaded ? formatter.format(moneyIncomesTotal) : 'подсчет...'}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ pr: 2 }} style={{ paddingLeft: 15 }}>Расход:</TableCell><TableCell sx={{ textAlign: 'right' }}>{transactionsLoaded ? formatter.format(moneyOutcomesTotal) : 'подсчет...'}</TableCell></TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                        <Box className={classes.crd} sx={{ flex: 'none', whiteSpace: 'nowrap', }}>
                            <CardHeader sx={{ display: "inline-block", }} title="Отпуск" className={classes.cardHeader} />
                            <Table sx={{ mt: 0.5, flex: '0' }}>
                                <TableBody>
                                    <TableRow><TableCell sx={{ pr: '10px !important' }}>Накоплено, дней:</TableCell><TableCell sx={{ textAlign: 'right' }}>{vacationBalanceLoaded ? vacationBalance : 'подсчет...'}</TableCell></TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                }
            </Box>
            {userProfile!.UserType == UserType.CustomerEmployee ?
                <Box className={classes.dashboardMain}>
                    <Card sx={{ backgroundColor: '#daffc4 !important' }}>
                        <Box className={classes.cardHeader} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <CardHeader title='Готовые задачи' />
                            <Box className={classes.issuesCount}>{myAvailableIssues.length}</Box>
                        </Box>
                        {!issuesLoaded ? <CircularProgress /> :
                            myAvailableIssues.length == 0 ? 'нет задач' :
                                <Table sx={{ tableLayout: 'fixed' }}>
                                    <TableBody>
                                        {myAvailableIssues.map(x =>
                                            <TableRow key={x.ID}>
                                                <TableCell className={[classes.issuePriority, 'issue-priority', `priority-${x.PriorityID}`].join(' ')}>
                                                    <Tooltip
                                                        title={getIssuePriorityNameRu(x.PriorityID)}
                                                        disableInteractive
                                                        placement='left'>
                                                        <FlagIcon />
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell className={classes.subject}>
                                                    <Link href={`./issues/${x.ID}`}>
                                                        {x.Subject}
                                                    </Link>
                                                </TableCell>
                                                <TableCell className={classes.client}>
                                                    <Link href={`./clients/${x.ClientID}`}>
                                                        {x.ClientName}
                                                    </Link>    
                                                </TableCell>
                                                <TableCell sx={{ width: '90px' }} className={classes.autocompleteDate}>
                                                    <Tooltip placement="left" arrow title="Задача будет автоматически закрыта в указанную дату">
                                                        <span>{x.AcceptanceDate ? moment(x.AcceptanceDate).format('DD.MM.yyyy') : 'ошибка'}</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell className={classes.planFact}>
                                                    <Tooltip placement="left" arrow title="План / Факт">
                                                        <>{moment.duration((x.EstimationHours ?? 0) * 60 * 60 * 1000).format("h", { trim: false })} / {moment.duration(x.TrackedHours * 60 * 60 * 1000).format("h", { trim: false })}</>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell className={classes.avatar}>
                                                    {x.AssignedEmployee ?
                                                        <Tooltip title={`Исполнитель: ${x.AssignedEmployee.Name}`} placement='right' disableInteractive >
                                                            <Link href={`./employees/${x.AssignedEmployee.ID}`}>
                                                                <Avatar
                                                                    src={x.AssignedEmployee.Avatar}
                                                                    sx={{
                                                                        border: 'none', fontWeight: 'bold', backgroundColor: 'orange', mr: 1, width: '30px', height: '30px', lineHeight: '30px',
                                                                        fontSize: 12,
                                                                        textTransform: 'uppercase'
                                                                    }}>
                                                                    {getPersonInitials({ LastName: x.AssignedEmployee.LastName, FirstName: x.AssignedEmployee.FirstName } as Person)}
                                                                </Avatar>
                                                            </Link>
                                                        </Tooltip>
                                                        :
                                                        <Tooltip title="исполнитель не назначен" placement='right' disableInteractive >
                                                            <Avatar sx={{ mr: 1, width: '30px', height: '30px', lineHeight: '30px', fontSize: 12, textTransform: 'uppercase' }} />
                                                        </Tooltip>
                                                    }
                                                </TableCell>
                                            </TableRow>)
                                        }
                                    </TableBody>
                                </Table>
                        }
                    </Card>
                    <Card>
                        <Box className={classes.cardHeader} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <CardHeader title='Требуют комментариев' />
                            <Box className={classes.issuesCount}>{myPausedForCommentsIssues.length}</Box>
                        </Box>
                        {!issuesLoaded ? <CircularProgress /> :
                            myPausedForCommentsIssues.length == 0 ? 'нет задач' :
                                <Table sx={{ tableLayout: 'fixed' }}>
                                    <TableBody>
                                        {myPausedForCommentsIssues.map(x =>
                                            <TableRow key={x.ID}>
                                                <TableCell className={[classes.issuePriority, 'issue-priority', `priority-${x.PriorityID}`].join(' ')}>
                                                    <Tooltip
                                                        title={getIssuePriorityNameRu(x.PriorityID)}
                                                        disableInteractive
                                                        placement='left'>
                                                        <FlagIcon />
                                                    </Tooltip>
                                                </TableCell>
                                                <Tooltip title={getIssueStateNameRu(x.StateID)} placement='left' disableInteractive >
                                                    <TableCell className={[classes.gridColumnIcon, 'grid-column-icon', 'center', `state-${x.StateID}`].join(' ')}>
                                                        &nbsp;
                                                    </TableCell>
                                                </Tooltip>
                                                <TableCell className={classes.subject}>
                                                    <Link href={`./issues/${x.ID}`}>
                                                        {x.Subject}
                                                    </Link>
                                                </TableCell>
                                                <TableCell className={classes.client}>
                                                    <Link href={`./clients/${x.ClientID}`}>
                                                        {x.ClientName}
                                                    </Link>
                                                </TableCell>
                                                <TableCell className={classes.planFact}>
                                                    {moment.duration((x.EstimationHours ?? 0) * 60 * 60 * 1000).format("h", { trim: false })} / {moment.duration(x.TrackedHours * 60 * 60 * 1000).format("h", { trim: false })}
                                                </TableCell>
                                                <TableCell className={classes.avatar}>
                                                    {x.AssignedEmployee ?
                                                        <Tooltip title={`Исполнитель: ${x.AssignedEmployee.Name}`} placement='right' disableInteractive >
                                                            <Link href={`./employees/${x.AssignedEmployee.ID}`}>
                                                                <Avatar
                                                                    src={x.AssignedEmployee.Avatar}
                                                                    sx={{
                                                                        border: 'none', fontWeight: 'bold', backgroundColor: 'orange', mr: 1, width: '30px', height: '30px', lineHeight: '30px',
                                                                        fontSize: 12,
                                                                        textTransform: 'uppercase'
                                                                    }}>
                                                                    {getPersonInitials({ LastName: x.AssignedEmployee.LastName, FirstName: x.AssignedEmployee.FirstName } as Person)}
                                                                </Avatar>
                                                            </Link>
                                                        </Tooltip>
                                                        :
                                                        <Tooltip title="исполнитель не назначен" placement='right' disableInteractive >
                                                            <Avatar sx={{ mr: 1, width: '30px', height: '30px', lineHeight: '30px', fontSize: 12, textTransform: 'uppercase' }} />
                                                        </Tooltip>
                                                    }
                                                </TableCell>
                                            </TableRow>)
                                        }
                                    </TableBody>
                                </Table>
                        }
                    </Card>
                    <Card>
                        <Box className={classes.cardHeader} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <CardHeader title='Я автор' />
                            <Box className={classes.issuesCount}>{myIssues.length}</Box>
                        </Box>
                        {!issuesLoaded ? <CircularProgress /> :
                            myIssues.length == 0 ? 'нет задач' :
                                <Table sx={{ maxWidth: '100%', tableLayout: 'fixed' }}>
                                    <TableBody>
                                        {myIssues.map(x =>
                                            <TableRow key={x.ID}>
                                                <TableCell className={[classes.issuePriority, 'issue-priority', `priority-${x.PriorityID}`].join(' ')}>
                                                    <Tooltip
                                                        title={getIssuePriorityNameRu(x.PriorityID)}
                                                        disableInteractive
                                                        placement='left'>
                                                        <FlagIcon />
                                                    </Tooltip>
                                                </TableCell>
                                                <Tooltip title={getIssueStateNameRu(x.StateID)} placement='left' disableInteractive >
                                                    <TableCell className={[classes.gridColumnIcon, 'grid-column-icon', 'center', `state-${x.StateID}`].join(' ')}>
                                                        &nbsp;
                                                    </TableCell>
                                                </Tooltip>
                                                <TableCell className={classes.subject}>
                                                    <Link href={`./issues/${x.ID}`}>
                                                        {x.Subject}
                                                    </Link>
                                                </TableCell>
                                                <TableCell className={classes.client}>
                                                    <Link href={`./clients/${x.ClientID}`}>
                                                        {x.ClientName}
                                                    </Link>
                                                </TableCell>
                                                <TableCell className={classes.planFact}>
                                                    {moment.duration((x.EstimationHours ?? 0) * 60 * 60 * 1000).format("h", { trim: false })} / {moment.duration(x.TrackedHours * 60 * 60 * 1000).format("h", { trim: false })}
                                                </TableCell>
                                                <TableCell className={classes.avatar}>
                                                    {x.AssignedEmployee ?
                                                        <Tooltip title={`Исполнитель: ${x.AssignedEmployee.Name}`} placement='right' disableInteractive >
                                                            <Link href={`./employees/${x.AssignedEmployee.ID}`}>
                                                                <Avatar
                                                                    src={x.AssignedEmployee.Avatar}
                                                                    sx={{
                                                                        border: 'none', fontWeight: 'bold', backgroundColor: 'orange', mr: 1, width: '30px', height: '30px', lineHeight: '30px',
                                                                        fontSize: 12,
                                                                        textTransform: 'uppercase'
                                                                    }}>
                                                                    {getPersonInitials({ LastName: x.AssignedEmployee.LastName, FirstName: x.AssignedEmployee.FirstName } as Person)}
                                                                </Avatar>
                                                            </Link>
                                                        </Tooltip>
                                                        :
                                                        <Tooltip title="исполнитель не назначен" placement='right' disableInteractive >
                                                            <Avatar sx={{ mr: 1, width: '30px', height: '30px', lineHeight: '30px', fontSize: 12, textTransform: 'uppercase' }} />
                                                        </Tooltip>
                                                    }
                                                </TableCell>
                                            </TableRow>)
                                        }
                                    </TableBody>
                                </Table>
                        }
                    </Card>
                </Box>
                :
                <Box className={classes.dashboardMain}>
                    <Box sx={{ p: 0, flex: 1, }}>
                        <Card className={classes.errors}>
                            <Box className={classes.cardHeader} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <CardHeader title="Ошибки" />
                                <Box className={classes.issuesCount}>{errors.length}</Box>
                            </Box>
                            <Box>{errors.length == 0 ? "нет ошибок за месяц"
                                :
                                !errorsLoaded ? <CircularProgress /> :
                                    <Table sx={{ maxWidth: '100%', }}>
                                        <TableBody>
                                            {errors.map(x =>
                                                <TableRow key={x.ID}>
                                                    <TableCell className={[classes.issuePriority, 'issue-priority', `priority-${x.PriorityID}`].join(' ')}>
                                                        <Tooltip
                                                            title={getIssuePriorityNameRu(x.PriorityID)}
                                                            disableInteractive
                                                            placement='left'>
                                                            <FlagIcon />
                                                        </Tooltip>
                                                    </TableCell>
                                                    <Tooltip title={getIssueStateNameRu(x.StateID)} placement='left' disableInteractive >
                                                        <TableCell className={[classes.gridColumnIcon, 'grid-column-icon', 'center', `state-${x.StateID}`].join(' ')}>
                                                            &nbsp;
                                                        </TableCell>
                                                    </Tooltip>
                                                    <TableCell className={classes.subject}>
                                                        <Link href={`./issues/${x.ID}`}>
                                                            {x.Subject}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell className={classes.client}>
                                                        <Link href={`./clients/${x.ClientID}`}>
                                                            {x.ClientName}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell className={classes.planFact}>
                                                        {moment.duration((x.EstimationHours ?? 0) * 60 * 60 * 1000).format("h", { trim: false })} / {moment.duration(x.TrackedHours * 60 * 60 * 1000).format("h", { trim: false })}
                                                    </TableCell>
                                                    <TableCell className={classes.avatar}>
                                                        <Tooltip title={`Автор: ${x.Author.Name}`} placement='right' disableInteractive >
                                                            <Link href={`./employees/${x.Author.ID}`}>
                                                                <Avatar
                                                                    src={x.Author.Avatar}
                                                                    sx={{
                                                                        border: 'none', fontWeight: 'bold', backgroundColor: 'orange', mr: 1, width: '30px', height: '30px', lineHeight: '30px',
                                                                        fontSize: 12,
                                                                        textTransform: 'uppercase'
                                                                    }}>
                                                                    {getPersonInitials({ LastName: x.Author.LastName, FirstName: x.Author.FirstName } as Person)}
                                                                </Avatar>
                                                            </Link>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>)
                                            }
                                        </TableBody>
                                    </Table>
                            }
                            </Box>
                        </Card>
                    </Box>
                    <Box sx={{ p: 0, flex: 1 }}>
                        <Card className={classes.assignedIssues}>
                            <Box className={classes.cardHeader} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <CardHeader title="Назначено мне" />
                                <Box className={classes.issuesCount}>{assignedIssues.length}</Box>
                            </Box>
                            {!issuesLoaded ? <CircularProgress /> :
                                <Table sx={{ maxWidth: '100%', tableLayout: 'fixed' }}>
                                    <TableBody>
                                        {assignedIssues.map(x =>
                                            <TableRow className={`grid-row-state-${x.StateID}`} key={x.ID}>
                                                <TableCell className={[classes.issuePriority, 'issue-priority', `priority-${x.PriorityID}`].join(' ')}>
                                                    <Tooltip
                                                        title={getIssuePriorityNameRu(x.PriorityID)}
                                                        disableInteractive
                                                        placement='left'>
                                                        <FlagIcon />
                                                    </Tooltip>
                                                </TableCell>
                                                <Tooltip title={getIssueStateNameRu(x.StateID)} placement='left' disableInteractive >
                                                    <TableCell className={[classes.gridColumnIcon, 'grid-column-icon', 'center', `state-${x.StateID}`].join(' ')}>
                                                        &nbsp;
                                                    </TableCell>
                                                </Tooltip>
                                                <TableCell className={classes.subject}>
                                                    <Link href={`./issues/${x.ID}`}>
                                                        {x.Subject}
                                                    </Link>
                                                </TableCell>
                                                <TableCell className={classes.client}>
                                                    <Link href={`./clients/${x.ClientID}`}>
                                                        {x.ClientName}
                                                    </Link>
                                                </TableCell>
                                                <TableCell className={classes.planFact}>
                                                    {moment.duration((x.EstimationHours ?? 0) * 60 * 60 * 1000).format("h", { trim: false })} / {moment.duration(x.TrackedHours * 60 * 60 * 1000).format("h", { trim: false })}
                                                </TableCell>
                                                <TableCell className={classes.avatar}>
                                                    <Tooltip title={`Автор: ${x.Author.Name}`} placement='right' disableInteractive >
                                                        <Link href={`./employees/${x.Author.ID}`}>
                                                            <Avatar
                                                                src={x.Author.Avatar}
                                                                sx={{
                                                                    border: 'none', fontWeight: 'bold', backgroundColor: 'orange', mr: 1, width: '30px', height: '30px', lineHeight: '30px',
                                                                    fontSize: 12,
                                                                    textTransform: 'uppercase'
                                                                }}>
                                                                {getPersonInitials({ LastName: x.Author.LastName, FirstName: x.Author.FirstName } as Person)}
                                                            </Avatar>
                                                        </Link>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>)
                                        }
                                    </TableBody>
                                </Table>
                            }
                        </Card>
                    </Box>                    
                    <Box sx={{ p: 0, flex: 1, }}>
                        {myAvailableIssues.length == 0 ? '' :
                            <Card sx={{ backgroundColor: '#daffc4 !important' }}>
                                <Box className={classes.cardHeader} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <CardHeader title='Готовые задачи' />
                                    <Box className={classes.issuesCount}>{myAvailableIssues.length}</Box>
                                </Box>
                                {!issuesLoaded ? <CircularProgress /> :
                                    myAvailableIssues.length == 0 ? 'нет задач' :
                                        <Table sx={{ tableLayout: 'fixed' }}>
                                            <TableBody>
                                                {myAvailableIssues.map(x =>
                                                    <TableRow key={x.ID}>
                                                        <TableCell className={[classes.issuePriority, 'issue-priority', `priority-${x.PriorityID}`].join(' ')}>
                                                            <Tooltip
                                                                title={getIssuePriorityNameRu(x.PriorityID)}
                                                                disableInteractive
                                                                placement='left'>
                                                                <FlagIcon />
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell className={classes.subject}>
                                                            <Link href={`./issues/${x.ID}`}>
                                                                {x.Subject}
                                                            </Link>
                                                        </TableCell>
                                                        <TableCell className={classes.client}>
                                                            <Link href={`./clients/${x.ClientID}`}>
                                                                {x.ClientName}
                                                            </Link>
                                                        </TableCell>
                                                        <TableCell sx={{ width: '90px' }} className={classes.autocompleteDate}>
                                                            <Tooltip placement="left" arrow title="Задача будет автоматически закрыта в указанную дату">
                                                                <span>{x.AcceptanceDate ? moment(x.AcceptanceDate).format('DD.MM.yyyy') : 'ошибка'}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell className={classes.planFact}>
                                                            <Tooltip placement="left" arrow title="План / Факт">
                                                                <>{moment.duration((x.EstimationHours ?? 0) * 60 * 60 * 1000).format("h", { trim: false })} / {moment.duration(x.TrackedHours * 60 * 60 * 1000).format("h", { trim: false })}</>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell className={classes.avatar}>
                                                            {x.AssignedEmployee ?
                                                                <Tooltip title={`Исполнитель: ${x.AssignedEmployee.Name}`} placement='right' disableInteractive >
                                                                    <Link href={`./employees/${x.AssignedEmployee.ID}`}>
                                                                        <Avatar
                                                                            src={x.AssignedEmployee.Avatar}
                                                                            sx={{
                                                                                border: 'none', fontWeight: 'bold', backgroundColor: 'orange', mr: 1, width: '30px', height: '30px', lineHeight: '30px',
                                                                                fontSize: 12,
                                                                                textTransform: 'uppercase'
                                                                            }}>
                                                                            {getPersonInitials({ LastName: x.AssignedEmployee.LastName, FirstName: x.AssignedEmployee.FirstName } as Person)}
                                                                        </Avatar>
                                                                    </Link>
                                                                </Tooltip>
                                                                :
                                                                <Tooltip title="исполнитель не назначен" placement='right' disableInteractive >
                                                                    <Avatar sx={{ mr: 1, width: '30px', height: '30px', lineHeight: '30px', fontSize: 12, textTransform: 'uppercase' }} />
                                                                </Tooltip>
                                                            }
                                                        </TableCell>
                                                    </TableRow>)
                                                }
                                            </TableBody>
                                        </Table>
                                }
                            </Card>
                        }
                        {myPausedForCommentsIssues.length == 0 ? '' :
                            <Card>
                                <Box className={classes.cardHeader} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <CardHeader title='Требуют комментариев' />
                                    <Box className={classes.issuesCount}>{myPausedForCommentsIssues.length}</Box>
                                </Box>
                                {!issuesLoaded ? <CircularProgress /> :
                                    myPausedForCommentsIssues.length == 0 ? 'нет задач' :
                                        <Table sx={{ tableLayout: 'fixed' }}>
                                            <TableBody>
                                                {myPausedForCommentsIssues.map(x =>
                                                    <TableRow key={x.ID}>
                                                        <TableCell className={[classes.issuePriority, 'issue-priority', `priority-${x.PriorityID}`].join(' ')}>
                                                            <Tooltip
                                                                title={getIssuePriorityNameRu(x.PriorityID)}
                                                                disableInteractive
                                                                placement='left'>
                                                                <FlagIcon />
                                                            </Tooltip>
                                                        </TableCell>
                                                        <Tooltip title={getIssueStateNameRu(x.StateID)} placement='left' disableInteractive >
                                                            <TableCell className={[classes.gridColumnIcon, 'grid-column-icon', 'center', `state-${x.StateID}`].join(' ')}>
                                                                &nbsp;
                                                            </TableCell>
                                                        </Tooltip>
                                                        <TableCell className={classes.subject}>
                                                            <Link href={`./issues/${x.ID}`}>
                                                                {x.Subject}
                                                            </Link>
                                                        </TableCell>
                                                        <TableCell className={classes.client}>
                                                            <Link href={`./clients/${x.ClientID}`}>
                                                                {x.ClientName}
                                                            </Link>
                                                        </TableCell>
                                                        <TableCell className={classes.planFact}>
                                                            {moment.duration((x.EstimationHours ?? 0) * 60 * 60 * 1000).format("h", { trim: false })} / {moment.duration(x.TrackedHours * 60 * 60 * 1000).format("h", { trim: false })}
                                                        </TableCell>
                                                        <TableCell className={classes.avatar}>
                                                            {x.AssignedEmployee ?
                                                                <Tooltip title={`Исполнитель: ${x.AssignedEmployee.Name}`} placement='right' disableInteractive >
                                                                    <Link href={`./employees/${x.AssignedEmployee.ID}`}>
                                                                        <Avatar
                                                                            src={x.AssignedEmployee.Avatar}
                                                                            sx={{
                                                                                border: 'none', fontWeight: 'bold', backgroundColor: 'orange', mr: 1, width: '30px', height: '30px', lineHeight: '30px',
                                                                                fontSize: 12,
                                                                                textTransform: 'uppercase'
                                                                            }}>
                                                                            {getPersonInitials({ LastName: x.AssignedEmployee.LastName, FirstName: x.AssignedEmployee.FirstName } as Person)}
                                                                        </Avatar>
                                                                    </Link>
                                                                </Tooltip>
                                                                :
                                                                <Tooltip title="исполнитель не назначен" placement='right' disableInteractive >
                                                                    <Avatar sx={{ mr: 1, width: '30px', height: '30px', lineHeight: '30px', fontSize: 12, textTransform: 'uppercase' }} />
                                                                </Tooltip>
                                                            }
                                                        </TableCell>
                                                    </TableRow>)
                                                }
                                            </TableBody>
                                        </Table>
                                }
                            </Card>
                        }
                    </Box>
                    <Box sx={{ p: 0, flex: 1 }}>
                        <Card className={classes.myIssues}>
                            <Box className={classes.cardHeader} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <CardHeader title='Я автор' />
                                <Box className={classes.issuesCount}>{myIssues.length}</Box>
                            </Box>
                            {!issuesLoaded ? <CircularProgress /> :
                                myIssues.length == 0 ? 'нет задач' :
                                    <Table sx={{ maxWidth: '100%', tableLayout: 'fixed' }}>
                                        <TableBody>
                                            {myIssues.map(x =>
                                                <TableRow className={`grid-row-state-${x.StateID}`} key={x.ID}>
                                                    <TableCell className={[classes.issuePriority, 'issue-priority', `priority-${x.PriorityID}`].join(' ')}>
                                                        <Tooltip
                                                            title={getIssuePriorityNameRu(x.PriorityID)}
                                                            disableInteractive
                                                            placement='left'>
                                                            <FlagIcon />
                                                        </Tooltip>
                                                    </TableCell>
                                                    <Tooltip title={getIssueStateNameRu(x.StateID)} placement='left' disableInteractive >
                                                        <TableCell className={[classes.gridColumnIcon, 'grid-column-icon', 'center', `state-${x.StateID}`].join(' ')}>
                                                            &nbsp;
                                                        </TableCell>
                                                    </Tooltip>
                                                    <TableCell className={classes.subject}>
                                                        <Link href={`./issues/${x.ID}`}>
                                                            {x.Subject}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell className={classes.client}>
                                                        <Link href={`./clients/${x.ClientID}`}>
                                                            {x.ClientName}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell className={classes.planFact}>
                                                        {moment.duration((x.EstimationHours ?? 0) * 60 * 60 * 1000).format("h", { trim: false })} / {moment.duration(x.TrackedHours * 60 * 60 * 1000).format("h", { trim: false })}
                                                    </TableCell>
                                                    <TableCell className={classes.avatar}>
                                                        {x.AssignedEmployee ?
                                                            <Tooltip title={`Исполнитель: ${x.AssignedEmployee.Name}`} placement='right' disableInteractive >
                                                                <Link href={`./employees/${x.AssignedEmployee.ID}`}>
                                                                    <Avatar
                                                                        src={x.AssignedEmployee.Avatar}
                                                                        sx={{
                                                                            border: 'none', fontWeight: 'bold', backgroundColor: 'orange', mr: 1, width: '30px', height: '30px', lineHeight: '30px',
                                                                            fontSize: 12,
                                                                            textTransform: 'uppercase'
                                                                        }}>
                                                                        {getPersonInitials({ LastName: x.AssignedEmployee.LastName, FirstName: x.AssignedEmployee.FirstName } as Person)}
                                                                    </Avatar>
                                                                </Link>
                                                            </Tooltip>
                                                            :
                                                            <Tooltip title="исполнитель не назначен" placement='right' disableInteractive >
                                                                <Avatar sx={{ mr: 1, width: '30px', height: '30px', lineHeight: '30px', fontSize: 12, textTransform: 'uppercase' }} />
                                                            </Tooltip>
                                                        }
                                                    </TableCell>
                                                </TableRow>)
                                            }
                                        </TableBody>
                                    </Table>
                            }
                        </Card>
                    </Box>
                </Box>
            }
        </Box>
    );
}